<template>
  <BaseDialog
    v-model="internalValue"
    @input="$emit('input', $event)"
    :title="$t('competition.sanction.add.label')"
    :cancelText="$t('cancel')"
    :confirmText="$t('confirm')"
    :loading="loading"
    :maxWidth="'500px'"
    :backgroundColor="'#f9efd5'"
    :confirmColor="'#1976d2'"
    :confirmDisabled="!formIsValid"
    @cancel="onCancel"
    @confirm="handleCreate"
  >
    <v-form ref="form" v-model="formIsValid">
      <v-autocomplete
        v-model="selectedCategory"
        :items="categories"
        :loading="isLoadingMatches"
        :disabled="isLoadingMatches"
        :search-input.sync="searchCategory"
        item-text="text"
        item-value="value"
        :label="$t('competition.sanction.select.category')"
        dense
        outlined
        clearable
        :rules="[requiredRule]"
        @change="onCategoryChange"
      />

      <!-- Match Selection -->
      <v-autocomplete
        v-if="matches.length || isLoadingMatches"
        v-model="selectedMatch"
        :items="matches"
        :loading="isLoadingMatchInfo"
        :disabled="isLoadingMatchInfo"
        :search-input.sync="searchMatch"
        item-text="text"
        item-value="value"
        :label="$t('competition.sanction.select.match')"
        dense
        outlined
        clearable
        :rules="[requiredRule]"
        @change="onMatchChange"
      />

      <!-- Team Selection -->
      <v-select
        v-if="teams.length"
        v-model="selectedTeam"
        :items="teams"
        item-value="value"
        item-text="text"
        :label="$t('competition.sanction.select.team')"
        dense
        outlined
        @change="onTeamChange"
        :rules="[requiredRule]"
      >
        <!-- Selection -->
        <template v-slot:selection="{ item }">
          <v-avatar size="32" class="mr-2" v-if="item.shield">
            <img :src="item.shield" :alt="item.text" />
          </v-avatar>
          {{ item.text }}
        </template>

        <!-- Dropdown item -->
        <template v-slot:item="{ item }">
          <v-list-tile-avatar v-if="item.shield">
            <img :src="item.shield" :alt="item.text" />
          </v-list-tile-avatar>
          <v-list-tile-content>
            <v-list-tile-title>{{ item.text }}</v-list-tile-title>
          </v-list-tile-content>
        </template>
      </v-select>


      <!-- Person Type Selector -->
      <v-select
        v-if="selectedTeam"
        v-model="selectedPersonType"
        :items="personTypeOptions"
        :label="$t('competition.sanction.select.person_type')"
        dense
        outlined
        @change="onPersonTypeChange"
        :rules="[requiredRule]" />

      <!-- Person Selection -->
      <v-select
        v-if="people.length"
        v-model="selectedPerson"
        :items="people"
        item-value="value"
        item-text="text"
        :label="$t('competition.sanction.select.person')"
        dense
        outlined
        :rules="[requiredRule]"
      >
        <!-- Selection -->
        <template v-slot:selection="{ item }">
          <v-avatar size="24" class="mr-2" style="background-color: #003784" v-if="item.number">
            <span class="subtitle-2 white--text">{{ item.number }}</span>
          </v-avatar>
          {{ item.text }}
        </template>

        <!-- Dropdown item -->
        <template v-slot:item="{ item }">
          <v-list-tile-avatar>
            <v-avatar size="32" style="background-color: #003784" v-if="item.number">
              <span class="subtitle-2 white--text">{{ item.number }}</span>
            </v-avatar>
          </v-list-tile-avatar>
          <v-list-tile-content>
            <v-list-tile-title>{{ item.text }}</v-list-tile-title>
          </v-list-tile-content>
        </template>
      </v-select>

      <!-- Sanction Type Selector -->
      <v-select
        v-if="selectedPerson && sanctions.length"
        v-model="selectedSanction"
        :items="sanctions"
        :label="$t('competition.sanction.select.type')"
        dense
        outlined
        :rules="[requiredRule]" />

      <!-- Observations Field -->
      <v-textarea
        v-if="selectedPerson"
        v-model="observations"
        :label="$t('competition.sanction.select.reason')"
        dense
        outlined
        rows="3"
        auto-grow
      />
    </v-form>
  </BaseDialog>
</template>

<script>
import BaseDialog from "@/components/ui/BaseDialog.vue";

export default {
  name: "CreateSanctionDialog",
  components: { BaseDialog },
  props: {
    value: { type: Boolean, required: true },
    loading: { type: Boolean, default: false }
  },
  data() {
    return {
      formIsValid: false,
      selectedCategory: null,
      selectedMatch: null,
      matches: [],
      searchCategory: null,
      searchMatch: null,
      selectedTeam: null,
      selectedPersonType: null,
      selectedPerson: null,
      selectedSanction: null,
      observations: "",
      matchInfo: null,
      isLoadingMatches: false,
      isLoadingMatchInfo: false,
    };
  },
  computed: {
    internalValue: {
      get () {
        return this.value
      },
      set (val) {
        this.$emit('input', val)
      }
    },
    categories() {
      const data = this.$store.getters["events/getCategoriesEventSelected"]();
      return data.map((category) => ({
        value: category.id,
        text: category.name,
      }));
    },
    teams() {
      if (!this.matchInfo || !this.matchInfo.teams) return [];
      return Object.values(this.matchInfo.teams).map((team) => ({
        value: team.id,
        text: team.name,
        shield: team.shield,
      }));
    },
    people() {
      if (!this.selectedTeam || 
          !this.matchInfo || 
          !this.matchInfo.teams || 
          !this.matchInfo.teams[this.selectedTeam]) return [];
      const selectedType = this.selectedPersonType;
      const teamData = this.matchInfo.teams[this.selectedTeam];
      const group = selectedType === 'staff' ? teamData.staff : teamData.players;

      return Object.values(group).map(person => ({
        value: person.id,
        text: `${person.name} ${person.surname}`,
        number: person.number || null,
      }));
    },
    sanctions() {
      const data = this.$store.getters["competition/sanctions/getPunishmentEvents"]();
      return data.map((item) => ({
        value: item.code,
        text: `${item.code}: ${this.$t(item.description)}`
      }));
    },
    personTypeOptions() {
      return [
        { value: 'player', text: this.$t('titles.player') },
        { value: 'staff', text: this.$t('titles.staff') }
      ];
    }
  },
  methods: {
    requiredRule(v) {
      return !!v || this.$t('required_field');
    },
    onCancel () {
      if (this.loading) return
      this.resetState();
      this.$emit('input', false) // close the dialog
    },
    async loadSanctions() {
      const existingSanctions = this.$store.getters["competition/sanctions/getPunishmentEvents"]();
      if (!existingSanctions.length) {
        await this.$store.dispatch("competition/sanctions/fetch_punishment_events");
      }
    },
    resetState() {
      this.selectedCategory = null;
      this.selectedMatch = null;
      this.matches = [];
      this.selectedTeam = null;
      this.selectedPersonType = null;
      this.selectedPerson = null;
      this.selectedSanction = null;
      this.observations = "";
      this.matchInfo = null;
      this.$refs.form.resetValidation();
    },
    async onCategoryChange(categoryId) {
      this.selectedMatch = null;
      this.matches = [];
      this.isLoadingMatches = true;

      const eventId = this.$store.getters["events/getCurrentEvent"]().id;
      await this.$store.dispatch("competition/matchs/load_by_category", {
        event_id: eventId,
        category_id: categoryId,
      });

      const data = this.$store.getters["competition/matchs/getMatchesByCategory"]();
      this.matches = Object.values(data)
        .filter((match) => match && typeof match === "object" && match.id && match.name)
        .map((match) => ({
          value: match.id,
          text: match.name,
        }));

      this.isLoadingMatches = false;
    },
    async onMatchChange(matchId) {
      this.selectedTeam = null;
      this.selectedPersonType = null;
      this.selectedPerson = null;
      this.selectedSanction = null;
      this.observations = "";
      this.matchInfo = null;
      this.isLoadingMatchInfo = true;

      await this.$store.dispatch("competition/matchs/find_by_id", matchId);
      this.matchInfo = this.$store.getters["competition/matchs/getFoundMatch"]();

      this.isLoadingMatchInfo = false;
    },
    onTeamChange() {
      this.selectedPersonType = null;
      this.selectedPerson = null;
      this.selectedSanction = null;
      this.observations = "";
    },
    onPersonTypeChange() {
      this.selectedPerson = null;
      this.selectedSanction = null;
      this.observations = "";
    },
    handleCreate() {
      this.$refs.form.validate();
      if (!this.formIsValid) {
        return;
      }

      const personType = this.selectedPersonType
      const type = this.selectedSanction
      const matchId = this.selectedMatch
      const personId = personType === 'staff' ? this.selectedPerson : null
      const playerId = personType === 'player' ? this.selectedPerson : null
      const comments = this.observations
      const data = {
        type,
        match_id: matchId,
        person_id: personId,
        player_id: playerId,
        comments
      }
      this.$emit('createSanction', data)
    },
  },
  created() {
    this.loadSanctions();
  },
};
</script>
